import { TlfViewModeConfig } from "aic";
import { CommentPanelStatus } from "components/common/comments/interface";
import { EditorBlocksSearchPanelStatus } from "components/tlfpage/TlfEditorBlocksSearchPanel";
import { CommentReadDto } from "functions/api/comments/interface";
import { eHierarchyLevel } from "functions/api/enums";
import { IRequestStatus } from "functions/api/interface";
import { TitlesFootnotesReadDto } from "functions/api/tlfItem/TitlesFootnotesReadDto";
import { TlfItemReadDto } from "functions/api/tlfItem/TlfItemReadDto";
import { PatchTlfItemDetails } from "functions/api/tlfItem/patchTlfItem";
import { SaveTlfItemDetails } from "functions/api/tlfItem/saveTlfItem";
import { Operation, Patch } from "rfc6902";
import { TlfContextModalWindow } from "../TlfContextProvider";

export type TlfItemReadDtoContext = TlfItemReadDto | null;

export interface TlfContextProps<T = TlfItemReadDtoContext | TitlesFootnotesReadDto> {
  tlfDataVersions: T[];
  ixTlfDataVersion: number;
  tlfData: T;

  // Utility items
  ixSaveVersion: number;
  changed: boolean;
  commentPanelStatus: CommentPanelStatus;
  editorBlocksSearchPanelStatus: EditorBlocksSearchPanelStatus;
  forcePushToParent: boolean;
  receivedMessages: TlfUpdateMessage[];

  // View mode
  tlfViewMode: TlfViewModeConfig;

  // Traffic lights
  showTrafficLights: boolean;

  // Modal windows
  showModalWindow: TlfContextModalWindow;

  // Subscription Status for Current User
  isSubscribed: boolean;

  // Flag to indicate whether there are pending hierarchy updates to be applied in the ARS listing, used to trigger confirmation modals when navigating away from the listing with unsaved changes
  delayedHierarchyUpdates: boolean;
  // ARS deferred update trigger token for delayed hierarchy updates in TLF ARS listing
  applyDelayedHierarchyUpdatesTrigger: number;
  // ARS deferred discard trigger token for delayed hierarchy updates in TLF ARS listing
  discardDelayedHierarchyUpdatesTrigger: number;
}

export interface ITlfStatusContext extends TlfContextProps {
  dispatch: React.Dispatch<ITlfAction>;
  patchRequestStatus: IRequestStatus<PatchTlfItemDetails<keyof TlfItemReadDto>>;
  patchTlf: <K extends keyof TlfItemReadDto>(key: K, newValue: TlfItemReadDto[K]) => Promise<void>;
  readOnly: boolean;
  resetSaveRequest: () => void;
  saveRequestStatus: IRequestStatus<SaveTlfItemDetails>;
  isSubscribed: boolean;

  /**
   * Saves the TLF Item with the given details.
   * @param azsolId - New AZSOL ID
   * @param version - New Version number
   * @param domain - New Domain
   * @param primaryHierarchyId - Hierarchy to save to
   * @param additionalInformation - New Additional information
   * @param copiedFromHierarchyLevel - Original object level
   * @param tocNumber - New Toc Number
   * @param sapReference - New SAP Reference
   * @returns Promise that resolves when the save is complete.
   */
  saveTlf: (
    azsolId?: string,
    version?: number,
    domain?: string,
    primaryHierarchyId?: string,
    additionalInformation?: string,
    copiedFromHierarchyLevel?: eHierarchyLevel,
    tocNumber?: string,
    sapReference?: string,
  ) => Promise<void>;

  // TODO: ?? Move comment panel status here?
}

export interface TlfUpdateMessage {
  messageId: string;
  updatedId: string;
  updateTS: Date;
}

export const APPLY_ARS_UPDATES = "APPLY_ARS_UPDATES";
export const DISCARD_ARS_UPDATES = "DISCARD_ARS_UPDATES";
export const CLOSE_MODAL_WINDOW = "CLOSE_MODAL_WINDOW";
export const LOAD_TLF = "LOAD_TLF";
export const PATCH_TLF_COMMENTS = "PATCH_TLF_COMMENTS";
export const PROCESS_SIGNALR_NOTIFICATION = "PROCESS_SIGNALR_NOTIFICATION";
export const REDO_TLF_CHANGE = "REDO_TLF_CHANGE";
export const REMOVE_TLF_COMMENT = "REMOVE_TLF_COMMENT";
export const SET_DELAYED_HIERARCHY_UPDATES = "SET_DELAYED_HIERARCHY_UPDATES";
export const SET_EDITOR_BLOCKS_SEARCH_PANEL = "SET_EDITOR_BLOCKS_SEARCH_PANEL";
export const SET_TLF_COMMENT_PANEL = "SET_TLF_COMMENT_PANEL";
export const SET_TLF_SUBSCRIPTION_STATUS = "SET_TLF_SUBSCRIPTION_STATUS";
export const SET_VIEW_MODE = "SET_VIEW_MODE";
export const SHOW_MODAL_WINDOW = "SHOW_MODAL_WINDOW";
export const TOGGLE_TRAFFIC_LIGHTS = "TOGGLE_TRAFFIC_LIGHTS";
export const UNDO_TLF_CHANGE = "UNDO_TLF_CHANGE";
export const UPDATE_TLF = "UPDATE_TLF";
export const UPDATE_TLF_COMMENT = "UPDATE_TLF_COMMENT";

export type TlfOperation =
  | typeof APPLY_ARS_UPDATES
  | typeof DISCARD_ARS_UPDATES
  | typeof CLOSE_MODAL_WINDOW
  | typeof LOAD_TLF
  | typeof PATCH_TLF_COMMENTS
  | typeof PROCESS_SIGNALR_NOTIFICATION
  | typeof REDO_TLF_CHANGE
  | typeof REMOVE_TLF_COMMENT
  | typeof SET_DELAYED_HIERARCHY_UPDATES
  | typeof SET_EDITOR_BLOCKS_SEARCH_PANEL
  | typeof SET_TLF_COMMENT_PANEL
  | typeof SET_TLF_SUBSCRIPTION_STATUS
  | typeof SET_VIEW_MODE
  | typeof SHOW_MODAL_WINDOW
  | typeof TOGGLE_TRAFFIC_LIGHTS
  | typeof UPDATE_TLF
  | typeof UPDATE_TLF_COMMENT
  | typeof UNDO_TLF_CHANGE;

export interface ITlfActionBase {
  operation: TlfOperation;
  calledFrom: string;
}

export interface ITlfActionApplyArsUpdates extends ITlfActionBase {
  operation: typeof APPLY_ARS_UPDATES;
}

export interface ITlfActionDiscardArsUpdates extends ITlfActionBase {
  operation: typeof DISCARD_ARS_UPDATES;
}

export interface ITlfActionCloseModalWindow extends ITlfActionBase {
  operation: typeof CLOSE_MODAL_WINDOW;
}

export interface ITlfActionLoadTlf extends ITlfActionBase {
  operation: typeof LOAD_TLF;
  tlfData: TlfItemReadDto | TitlesFootnotesReadDto;
  isSubscribed: boolean;
}

export interface ITlfActionPatchTlfComments extends ITlfActionBase {
  operation: typeof PATCH_TLF_COMMENTS;
  patch: Patch;
}

export interface ITlfActionProcessSignalRNotification extends ITlfActionBase {
  operation: typeof PROCESS_SIGNALR_NOTIFICATION;
  patch: Operation[];
}

export interface ITlfActionRedoTlfChange extends ITlfActionBase {
  operation: typeof REDO_TLF_CHANGE;
  steps?: number;
}

export interface ITlfActionRemoveTlfComment extends ITlfActionBase {
  operation: typeof REMOVE_TLF_COMMENT;
  commentId: string;
}

export interface ITlfActionSetDelayedHierarchyUpdates extends ITlfActionBase {
  operation: typeof SET_DELAYED_HIERARCHY_UPDATES;
  delayedHierarchyUpdates: boolean;
}

export interface ITlfActionSetEditorBlocksSearchPanel extends ITlfActionBase {
  operation: typeof SET_EDITOR_BLOCKS_SEARCH_PANEL;
  editorBlocksSearchPanelStatus: EditorBlocksSearchPanelStatus;
}

export interface ITlfActionSetSubscriptionStatus extends ITlfActionBase {
  operation: typeof SET_TLF_SUBSCRIPTION_STATUS;
  isSubscribed: boolean;
}

export interface ITlfActionShowModalWindow extends ITlfActionBase {
  operation: typeof SHOW_MODAL_WINDOW;
  modalWindow: TlfContextModalWindow;
}

export interface ITlfActionSetViewMode extends ITlfActionBase {
  operation: typeof SET_VIEW_MODE;
  tlfViewMode: TlfViewModeConfig;
}

export interface ITlfActionSetTlfCommentPanel extends ITlfActionBase {
  operation: typeof SET_TLF_COMMENT_PANEL;
  commentPanelStatus: CommentPanelStatus;
}

export interface ITlfActionToggleTrafficLights extends ITlfActionBase {
  operation: typeof TOGGLE_TRAFFIC_LIGHTS;
  showTrafficLights: boolean;
}

export interface ITlfActionUndoTlfChange extends ITlfActionBase {
  operation: typeof UNDO_TLF_CHANGE;
  steps?: number;
}

export interface ITlfActionUpdateTlf extends ITlfActionBase {
  operation: typeof UPDATE_TLF;
  tlfData: Partial<TlfItemReadDto>;
  doNotAddUndo?: boolean;
  newSavePoint?: boolean;
  reset?: boolean;
}

export interface ITlfActionUpdateTlfComment extends ITlfActionBase {
  operation: typeof UPDATE_TLF_COMMENT;
  comment: CommentReadDto;
}

export type ITlfAction =
  | ITlfActionApplyArsUpdates
  | ITlfActionDiscardArsUpdates
  | ITlfActionCloseModalWindow
  | ITlfActionLoadTlf
  | ITlfActionPatchTlfComments
  | ITlfActionProcessSignalRNotification
  | ITlfActionRedoTlfChange
  | ITlfActionRemoveTlfComment
  | ITlfActionSetDelayedHierarchyUpdates
  | ITlfActionSetEditorBlocksSearchPanel
  | ITlfActionSetSubscriptionStatus
  | ITlfActionSetTlfCommentPanel
  | ITlfActionSetViewMode
  | ITlfActionShowModalWindow
  | ITlfActionToggleTrafficLights
  | ITlfActionUndoTlfChange
  | ITlfActionUpdateTlf
  | ITlfActionUpdateTlfComment;
